import {ImageType} from "../../definitions/types";
import {MediaType} from "../../definitions/enums";

export class ImageModel {
    private readonly _image: ImageType;

    constructor(public readonly image: ImageType) {
        this._image = image;
    }

    /**
     * @property mediaType
     * @description Returns the Image media type.
     * @returns {string}
     */
    public get mediaType(): MediaType {
        return MediaType.Image;
    }

    /**
     * @method type
     * @description Returns the type if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get type(): string | undefined {
        return this._image.type;
    }

    /**
     * @method filename
     * @description Returns the filename if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get filename(): string | undefined {
        return this._image.filename;
    }

    /**
     * @method alt
     * @description Returns the alt if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get alt(): string | undefined {
        return this._image.alt;
    }

    /**
     * @method title
     * @description Returns the title if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get title(): string | undefined {
        return this._image.title;
    }

    /**
     * @method description
     * @description Returns the description if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get description(): string | undefined {
        return this._image.description;
    }

    /**
     * @method isRaw
     * @description Returns true if the image type is a raw image type, otherwise returns false.
     */
    public get isRaw(): boolean {
        return this._image.isRaw;
    }

    /**
     * @method isWide
     * @description Returns true if the image is wide, otherwise returns false.
     * @returns {boolean}
     */
    public get isWide(): boolean {
        return this._image.isWide;
    }

    /**
     * @method width
     * @description Returns the width of the image.
     * @returns {number | undefined}
     */
    public get width(): number | undefined {
        return this._image.width;
    }

    /**
     * @method height
     * @description Returns the height of the image.
     * @returns {number | undefined}
     */
    public get height(): number | undefined {
        return this._image.height;
    }

    /**
     * @method url
     * @description Returns the url if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get url(): string | undefined {
        return this._image.url;
    }

    /**
     * @method zoomUrl
     * @description Returns the zoomUrl if the image is raw, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get zoomUrl(): string | undefined {
        return this._image.isRaw ? this._image.zoomUrl : undefined;
    }

    /**
     * @method previewUrl
     * @description Returns the previewUrl if the image is raw, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get previewUrl(): string | undefined {
        return this._image.isRaw ? this._image.previewUrl : undefined;
    }

    /**
     * @method thumbnailUrl
     * @description Returns the thumbnailUrl if available, otherwise returns undefined.
     * @returns {string | undefined}
     */
    public get thumbnailUrl(): string | undefined {
        return this._image.thumbnailUrl;
    }
}